import { all, call, put, takeLatest } from "redux-saga/effects";

import { showNoti } from "redux/actions";

import {
  FETCH_PERMISSION_MATRIX,
  INIT_PERMISSION_MATRIX,
  UPDATE_PERMISSION_MATRIX,
} from "./action";
import {
  fetchPermissionMatrixApi,
  initPermissionMatrixApi,
  updatePermissionMatrixApi,
} from "./api";

const getResponseMessage = (response, fallbackMessage) =>
  response && response.data && response.data.message
    ? response.data.message
    : fallbackMessage;

const getRequestErrorMessage = (error, fallbackMessage) =>
  error &&
  error.response &&
  error.response.data &&
  error.response.data.message
    ? error.response.data.message
    : fallbackMessage;

function* notifyError(message, onError, details) {
  yield put(showNoti({ type: "error", message }));
  if (typeof onError === "function") yield call(onError, message, details);
}

function* fetchPermissionMatrixSaga({ payload = {} }) {
  const { onSuccess, onError, errorMessage, invalidResponseMessage } = payload;

  try {
    const response = yield call(fetchPermissionMatrixApi);

    if (response && response.data && response.data.success === false) {
      yield call(
        notifyError,
        getResponseMessage(response, errorMessage),
        onError,
        { status: response.status },
      );
      return;
    }

    const permissionMatrix =
      response && response.data && response.data.data
        ? response.data.data.permissionMatrix
        : null;

    if (!permissionMatrix || typeof permissionMatrix !== "object") {
      yield call(notifyError, invalidResponseMessage, onError);
      return;
    }

    if (typeof onSuccess === "function") {
      yield call(onSuccess, permissionMatrix);
    }
  } catch (error) {
    yield call(
      notifyError,
      getRequestErrorMessage(error, errorMessage),
      onError,
      { status: error && error.response ? error.response.status : null },
    );
  }
}

function* initPermissionMatrixSaga({ payload = {} }) {
  const {
    onSuccess,
    onError,
    successMessage,
    errorMessage,
    invalidResponseMessage,
  } = payload;

  try {
    const response = yield call(initPermissionMatrixApi);

    if (response && response.data && response.data.success === false) {
      yield call(
        notifyError,
        getResponseMessage(response, errorMessage),
        onError,
      );
      return;
    }

    const refreshedResponse = yield call(fetchPermissionMatrixApi);

    if (
      refreshedResponse &&
      refreshedResponse.data &&
      refreshedResponse.data.success === false
    ) {
      yield call(
        notifyError,
        getResponseMessage(refreshedResponse, errorMessage),
        onError,
      );
      return;
    }

    const refreshedMatrix =
      refreshedResponse &&
      refreshedResponse.data &&
      refreshedResponse.data.data
        ? refreshedResponse.data.data.permissionMatrix
        : null;

    if (!refreshedMatrix || typeof refreshedMatrix !== "object") {
      yield call(notifyError, invalidResponseMessage, onError);
      return;
    }

    if (typeof onSuccess === "function") {
      yield call(onSuccess, refreshedMatrix);
    }
    yield put(showNoti({ type: "success", message: successMessage }));
  } catch (error) {
    yield call(
      notifyError,
      getRequestErrorMessage(error, errorMessage),
      onError,
    );
  }
}

function* updatePermissionMatrixSaga({ payload = {} }) {
  const {
    permissionMatrix,
    onSuccess,
    onError,
    successMessage,
    errorMessage,
  } = payload;

  try {
    const response = yield call(updatePermissionMatrixApi, permissionMatrix);

    if (response && response.data && response.data.success === false) {
      yield call(
        notifyError,
        getResponseMessage(response, errorMessage),
        onError,
      );
      return;
    }

    const refreshedResponse = yield call(fetchPermissionMatrixApi);

    if (
      refreshedResponse &&
      refreshedResponse.data &&
      refreshedResponse.data.success === false
    ) {
      yield call(
        notifyError,
        getResponseMessage(refreshedResponse, errorMessage),
        onError,
      );
      return;
    }

    const refreshedMatrix =
      refreshedResponse &&
      refreshedResponse.data &&
      refreshedResponse.data.data
        ? refreshedResponse.data.data.permissionMatrix
        : null;

    if (!refreshedMatrix || typeof refreshedMatrix !== "object") {
      yield call(notifyError, errorMessage, onError);
      return;
    }

    if (typeof onSuccess === "function") {
      yield call(onSuccess, refreshedMatrix);
    }
    yield put(showNoti({ type: "success", message: successMessage }));
  } catch (error) {
    yield call(
      notifyError,
      getRequestErrorMessage(error, errorMessage),
      onError,
    );
  }
}

export default function* permissionsSaga() {
  yield all([
    takeLatest(FETCH_PERMISSION_MATRIX, fetchPermissionMatrixSaga),
    takeLatest(INIT_PERMISSION_MATRIX, initPermissionMatrixSaga),
    takeLatest(UPDATE_PERMISSION_MATRIX, updatePermissionMatrixSaga),
  ]);
}
